import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axiosClient from "../axios";

const UpgradeToPremium = () => {
  const [pricingData, setPricingData] = useState({
    price: "",
    tax: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axiosClient.get("/api/option/pricing");
        if (response.data?.option_value) {
          const data = JSON.parse(response.data.option_value);
          setPricingData({
            price: data.price || "",
            tax: data.tax || "",
          });
        }
      } catch (err) {
        setError("Failed to load pricing information");
        console.error("Pricing API error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  // Toggle accordion item
  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  // Calculate price with tax
  const calculatePriceWithTax = (basePrice) => {
    if (!basePrice || !pricingData.tax) return basePrice;
    const priceNum = parseFloat(basePrice.replace(/[^\d.]/g, ""));
    const taxAmount = priceNum * (parseFloat(pricingData.tax) / 100);
    return `₹${Math.round(priceNum + taxAmount)}`;
  };

  const pricingPlans = [
    {
      name: "One-Time Purchase",
      price: pricingData.price
        ? calculatePriceWithTax(pricingData.price)
        : "Loading...",
      originalPrice: pricingData.price,
      period: "one-time",
      popular: true,
      features: [
        "Lifetime access to all courses",
        "Download all resources",
        "Priority support",
        "Certificate of completion",
        "Free updates forever",
      ],
      note: pricingData.tax ? `Includes ${pricingData.tax}% tax` : "",
    },
  ];

  // FAQ data
  const faqItems = [
    {
      question: "What does lifetime access mean?",
      answer:
        "Lifetime access means you pay once and get access to all current and future courses forever. There are no recurring charges or hidden fees.",
    },
    {
      question: "Is there a money-back guarantee?",
      answer:
        "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with the premium features, we'll refund your payment.",
    },
    {
      question: "Will I be charged tax?",
      answer: pricingData.tax
        ? `Yes, the displayed price includes ${pricingData.tax}% tax as required by regulations.`
        : "Tax may be applied depending on your location and local regulations.",
    },
  ];

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-warning" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading pricing information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="text-center mb-5">
        <div
          className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
          style={{ width: "80px", height: "80px" }}
        >
          <i className="bi bi-award-fill text-warning fs-1"></i>
        </div>
        <h1 className="display-5 fw-bold text-dark mb-3">Upgrade to Premium</h1>
        <p className="lead text-muted mx-auto" style={{ maxWidth: "600px" }}>
          Get lifetime access to all premium features with a one-time payment
        </p>
      </div>

      <div className="row justify-content-center">
        {pricingPlans.map((plan, index) => (
          <div key={index} className="col-lg-6 col-md-8 mb-4">
            <div
              className={`card h-100 border-0 shadow-lg transition-all hover-lift ${
                plan.popular ? "border-warning border-3" : ""
              }`}
            >
              {plan.popular && (
                <div className="card-header bg-warning text-dark text-center py-3">
                  <span className="fw-bold">
                    <i className="bi bi-star-fill me-2"></i>BEST VALUE
                  </span>
                </div>
              )}
              <div className="card-body p-5 text-center">
                <h5 className="card-title fw-semibold text-dark">
                  {plan.name}
                </h5>
                <div className="my-4">
                  <span className="display-3 fw-bold text-dark">
                    {plan.price}
                  </span>
                  <div className="text-muted mt-2">One-time payment</div>
                </div>

                {plan.note && (
                  <div className="badge bg-success bg-opacity-10 text-success mb-3 px-3 py-2">
                    <small>{plan.note}</small>
                  </div>
                )}

                <ul className="list-unstyled mb-4">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="mb-3 py-1">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/premium/checkout"
                  className={`btn btn-warning btn-lg w-100 fw-semibold py-3`}
                >
                  Get Premium Access
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Comparison */}
      <div className="row justify-content-center mt-5">
        <div className="col-lg-10">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light py-4">
              <h3 className="h4 mb-0 text-center">
                Premium Features Comparison
              </h3>
            </div>
            <div className="card-body p-4">
              <div className="table-responsive">
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      <th
                        className="fw-semibold text-dark"
                        style={{ width: "60%" }}
                      >
                        Feature
                      </th>
                      <th
                        className="text-center fw-semibold text-dark"
                        style={{ width: "20%" }}
                      >
                        Free
                      </th>
                      <th
                        className="text-center fw-semibold text-dark"
                        style={{ width: "20%" }}
                      >
                        Premium
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Access to all courses</td>
                      <td className="text-center">
                        <i className="bi bi-x-circle text-danger fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                    <tr>
                      <td>Download resources</td>
                      <td className="text-center">
                        <i className="bi bi-x-circle text-danger fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                    <tr>
                      <td>Priority support</td>
                      <td className="text-center">
                        <i className="bi bi-x-circle text-danger fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                    <tr>
                      <td>Certificate of completion</td>
                      <td className="text-center">
                        <i className="bi bi-x-circle text-danger fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                    <tr>
                      <td>Mobile app access</td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                    <tr>
                      <td>Lifetime updates</td>
                      <td className="text-center">
                        <i className="bi bi-x-circle text-danger fs-5"></i>
                      </td>
                      <td className="text-center">
                        <i className="bi bi-check-circle text-success fs-5"></i>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="row justify-content-center mt-5">
        <div className="col-lg-8">
          <div className="text-center mb-4">
            <h3 className="h4">Frequently Asked Questions</h3>
          </div>
          <div className="accordion" id="premiumFAQ">
            {faqItems.map((faq, index) => (
              <div
                key={index}
                className="accordion-item border-0 shadow-sm mb-3"
              >
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${
                      openAccordion === index ? "" : "collapsed"
                    } fw-semibold`}
                    type="button"
                    onClick={() => toggleAccordion(index)}
                    aria-expanded={openAccordion === index}
                  >
                    {faq.question}
                  </button>
                </h2>
                <div
                  className={`accordion-collapse ${
                    openAccordion === index ? "show" : "collapse"
                  }`}
                >
                  <div className="accordion-body">{faq.answer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeToPremium;
